// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
import { useEffect } from "react";
/** Controls a tooltip can belong to. */
const CONTROLS = "button, a[href], [role=button], [role=tab], [role=menuitem], [role=switch], [role=checkbox], [title]";
/** How long to rest on a control whose visible text names it before its extra detail shows. */
const DETAIL_DELAY_MS = 600;
/** Height of the strip the macOS window buttons sit in when the page has no title bar. */
const TITLEBAR_HEIGHT = 40;
/** Text a person can see in el (screen-reader-only text excluded). */
function visibleText(el) {
    let text = "";
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent ?? "";
            return;
        }
        if (node instanceof HTMLElement && (node.classList.contains("sr-only") || node.hidden || node.getAttribute("aria-hidden") === "true"))
            return;
        node.childNodes.forEach(walk);
    };
    walk(el);
    return text.trim();
}
/**
 * Every icon-only control gets a tooltip with its name (aria-label, else title), shown at
 * once on hover or keyboard focus, and native title tooltips (which wait a second) are shown
 * the same way instead. A title that only adds detail to visible text (a file tree row's
 * path) waits a moment on hover, so sweeping across a list doesn't flash one per row.
 * Controls with their own Tooltip are left to it. Mount once.
 */
export function AutoTooltips() {
    useEffect(() => {
        const tip = document.createElement("div");
        tip.setAttribute("role", "tooltip");
        tip.className = "pointer-events-none fixed z-[100] max-w-xs rounded-md bg-fg px-2 py-1 text-xs font-medium text-bg shadow-overlay";
        tip.style.display = "none";
        document.body.append(tip);
        let current = null;
        let timer;
        const hide = () => {
            clearTimeout(timer);
            if (current?.dataset.autoTitle !== undefined) {
                current.setAttribute("title", current.dataset.autoTitle);
                delete current.dataset.autoTitle;
            }
            current = null;
            tip.style.display = "none";
        };
        const place = (el, label) => {
            tip.textContent = label;
            tip.style.left = "0px";
            tip.style.display = "block";
            const r = el.getBoundingClientRect();
            const t = tip.getBoundingClientRect();
            // On the macOS desktop app the window buttons float over the top strip of the page.
            const reserved = document.documentElement.dataset.titlebar === "overlay" ? TITLEBAR_HEIGHT : 0;
            const below = r.top - t.height - 8 < reserved;
            const top = below ? r.bottom + 6 : r.top - t.height - 6;
            const left = Math.min(Math.max(4, r.left + r.width / 2 - t.width / 2), window.innerWidth - t.width - 4);
            tip.style.top = `${top}px`;
            tip.style.left = `${left}px`;
        };
        const show = (target, byFocus) => {
            const el = target instanceof Element ? target.closest(CONTROLS) : null;
            if (el === current)
                return;
            hide();
            if (!el || el.closest("[data-has-tooltip]") || el.closest(".cm-content"))
                return;
            const title = el.getAttribute("title") ?? "";
            const label = (el.getAttribute("aria-label") ?? "").trim() || title.trim();
            // Visible text already names it, unless the title says something more.
            const text = visibleText(el);
            if (!label || (text !== "" && (title === "" || text === title.trim())))
                return;
            if (title) {
                // The native tooltip would show too, a second later.
                el.dataset.autoTitle = title;
                el.removeAttribute("title");
            }
            current = el;
            if (text === "" || byFocus)
                place(el, label);
            else
                timer = setTimeout(() => {
                    if (current === el && el.isConnected)
                        place(el, label);
                }, DETAIL_DELAY_MS);
        };
        const over = (e) => {
            show(e.target, false);
        };
        const focus = (e) => {
            // A click focuses too; only keyboard focus should bring a tooltip up.
            if (e.target instanceof Element && e.target.matches(":focus-visible"))
                show(e.target, true);
        };
        const out = (e) => {
            if (current && !(e.relatedTarget instanceof Node && current.contains(e.relatedTarget)))
                hide();
        };
        // A press hides the tooltip but keeps the control's title off until the pointer leaves,
        // so the native one doesn't come up a second later.
        const press = () => {
            clearTimeout(timer);
            tip.style.display = "none";
        };
        const key = (e) => {
            if (e.key === "Escape")
                hide();
        };
        document.addEventListener("pointerover", over, true);
        document.addEventListener("focusin", focus, true);
        document.addEventListener("pointerout", out, true);
        document.addEventListener("focusout", out, true);
        document.addEventListener("pointerdown", press, true);
        document.addEventListener("scroll", hide, true);
        document.addEventListener("keydown", key, true);
        return () => {
            hide();
            tip.remove();
            document.removeEventListener("pointerover", over, true);
            document.removeEventListener("focusin", focus, true);
            document.removeEventListener("pointerout", out, true);
            document.removeEventListener("focusout", out, true);
            document.removeEventListener("pointerdown", press, true);
            document.removeEventListener("scroll", hide, true);
            document.removeEventListener("keydown", key, true);
        };
    }, []);
    return null;
}
//# sourceMappingURL=AutoTooltips.js.map